const TRANSFORMERS_MODULE_URL = "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.8.1/+esm";
const DEFAULT_MODEL = "onnx-community/whisper-tiny";
const TARGET_SAMPLE_RATE = 16000;
const SILENCE_MS = 850;
const MAX_UTTERANCE_MS = 14000;
const MIN_UTTERANCE_MS = 280;
const SPEECH_RMS = 0.012;

let sharedWorker = null;
let workerRequestId = 0;
const pendingWorkerRequests = new Map();

function createWorker() {
  const source = `
    import { pipeline, env } from ${JSON.stringify(TRANSFORMERS_MODULE_URL)};

    env.allowLocalModels = false;
    env.useBrowserCache = true;

    let transcriberPromise = null;
    let activeDevice = null;

    async function getTranscriber() {
      if (transcriberPromise) return transcriberPromise;
      activeDevice = self.navigator && self.navigator.gpu ? "webgpu" : "wasm";
      const loading = pipeline(
        "automatic-speech-recognition",
        ${JSON.stringify(DEFAULT_MODEL)},
        {
          device: activeDevice,
          dtype: activeDevice === "webgpu" ? "fp16" : "q8",
          progress_callback: (progress) => {
            self.postMessage({ type: "model-progress", progress });
          },
        },
      ).catch(async (error) => {
        if (activeDevice !== "webgpu") throw error;
        activeDevice = "wasm";
        return pipeline(
          "automatic-speech-recognition",
          ${JSON.stringify(DEFAULT_MODEL)},
          { device: "wasm", dtype: "q8" },
        );
      });
      transcriberPromise = loading.catch((error) => {
        transcriberPromise = null;
        throw error;
      });
      return transcriberPromise;
    }

    self.onmessage = async (event) => {
      const message = event.data || {};
      if (message.type === "warmup") {
        try {
          await getTranscriber();
          self.postMessage({ type: "ready", requestId: message.requestId, device: activeDevice });
        } catch (error) {
          self.postMessage({ type: "error", requestId: message.requestId, error: String(error?.message || error) });
        }
        return;
      }
      if (message.type !== "transcribe") return;
      try {
        const transcriber = await getTranscriber();
        const audio = new Float32Array(message.audio);
        const output = await transcriber(audio, {
          language: "spanish",
          task: "transcribe",
          return_timestamps: false,
        });
        self.postMessage({
          type: "result",
          requestId: message.requestId,
          text: String(output?.text || "").trim(),
          device: activeDevice,
        });
      } catch (error) {
        self.postMessage({ type: "error", requestId: message.requestId, error: String(error?.message || error) });
      }
    };
  `;
  const blob = new Blob([source], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url, { type: "module", name: "owner-whisper-stt" });
  URL.revokeObjectURL(url);
  worker.onmessage = (event) => {
    const message = event.data || {};
    if (message.type === "model-progress") {
      window.dispatchEvent(new CustomEvent("wp-owner-stt-progress", { detail: message.progress || {} }));
      return;
    }
    const pending = pendingWorkerRequests.get(message.requestId);
    if (!pending) return;
    pendingWorkerRequests.delete(message.requestId);
    if (message.type === "error") pending.reject(new Error(message.error || "No pude cargar el motor local de voz."));
    else pending.resolve(message);
  };
  worker.onerror = (event) => {
    const error = new Error(event?.message || "Falló el worker local de reconocimiento de voz.");
    for (const pending of pendingWorkerRequests.values()) pending.reject(error);
    pendingWorkerRequests.clear();
    try { worker.terminate(); } catch { /* noop */ }
    if (sharedWorker === worker) sharedWorker = null;
  };
  return worker;
}

function getWorker() {
  if (!sharedWorker) sharedWorker = createWorker();
  return sharedWorker;
}

function workerRequest(type, payload = {}, transfer = []) {
  const worker = getWorker();
  const requestId = ++workerRequestId;
  return new Promise((resolve, reject) => {
    pendingWorkerRequests.set(requestId, { resolve, reject });
    worker.postMessage({ type, requestId, ...payload }, transfer);
  });
}

export function isOwnerLocalSttSupported() {
  return typeof window !== "undefined"
    && typeof Worker !== "undefined"
    && typeof AudioContext !== "undefined"
    && Boolean(navigator?.mediaDevices?.getUserMedia);
}

export async function warmOwnerLocalStt() {
  if (!isOwnerLocalSttSupported()) throw new Error("Este navegador no puede ejecutar el motor local de voz.");
  return workerRequest("warmup");
}

function mixToMono(audioBuffer) {
  const length = audioBuffer.length;
  const channels = Math.max(1, audioBuffer.numberOfChannels || 1);
  const mono = new Float32Array(length);
  for (let channel = 0; channel < channels; channel += 1) {
    const data = audioBuffer.getChannelData(channel);
    for (let index = 0; index < length; index += 1) mono[index] += data[index] / channels;
  }
  return mono;
}

function resampleLinear(input, sourceRate, targetRate = TARGET_SAMPLE_RATE) {
  if (sourceRate === targetRate) return input;
  const ratio = sourceRate / targetRate;
  const length = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(length);
  for (let index = 0; index < length; index += 1) {
    const position = index * ratio;
    const left = Math.floor(position);
    const right = Math.min(input.length - 1, left + 1);
    const mix = position - left;
    output[index] = input[left] * (1 - mix) + input[right] * mix;
  }
  return output;
}

function normalizedError(error) {
  const name = String(error?.name || "");
  if (name === "NotAllowedError" || name === "SecurityError") return "not-allowed";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "audio-capture";
  if (name === "NotReadableError" || name === "TrackStartError") return "audio-capture";
  return "local-stt";
}

function makeResultEvent(text) {
  const alternative = { transcript: text, confidence: 1 };
  const row = [alternative];
  row.isFinal = true;
  return { resultIndex: 0, results: [row] };
}

export class OwnerLocalSpeechRecognition {
  constructor() {
    this.lang = "es-AR";
    this.continuous = false;
    this.interimResults = false;
    this.maxAlternatives = 1;
    this.onstart = null;
    this.onend = null;
    this.onerror = null;
    this.onresult = null;
    this._stream = null;
    this._audioContext = null;
    this._source = null;
    this._processor = null;
    this._chunks = [];
    this._startedAt = 0;
    this._speechStartedAt = 0;
    this._lastSpeechAt = 0;
    this._heardSpeech = false;
    this._stopping = false;
    this._aborted = false;
    this._processing = false;
    this._startPromise = null;
    this._finishPromise = null;
    this._lifecycleToken = 0;
  }

  start() {
    if (this._startPromise) return this._startPromise;
    if (this._stream || this._processing || this._stopping) return Promise.resolve();

    this._aborted = false;
    this._stopping = false;
    const lifecycleToken = this._lifecycleToken + 1;
    this._lifecycleToken = lifecycleToken;

    const pending = this._startSession(lifecycleToken);
    this._startPromise = pending;
    return pending.finally(() => {
      if (this._startPromise === pending) this._startPromise = null;
    });
  }

  async _startSession(lifecycleToken) {
    let pendingStream = null;
    try {
      pendingStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      // getUserMedia no es cancelable. Si stop/abort/desmontaje ocurrió mientras
      // el permiso estaba pendiente, el stream que llega tarde se cierra acá y
      // jamás se convierte en una sesión huérfana.
      if (this._lifecycleToken !== lifecycleToken || this._stopping || this._aborted) {
        pendingStream.getTracks?.().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
        return;
      }

      this._stream = pendingStream;
      pendingStream = null;
      this._audioContext = new AudioContext({ latencyHint: "interactive" });
      await this._audioContext.resume();

      if (this._lifecycleToken !== lifecycleToken || this._stopping || this._aborted) {
        await this._cleanup();
        return;
      }

      this._source = this._audioContext.createMediaStreamSource(this._stream);
      this._processor = this._audioContext.createScriptProcessor(4096, 1, 1);
      this._chunks = [];
      this._startedAt = performance.now();
      this._speechStartedAt = 0;
      this._lastSpeechAt = 0;
      this._heardSpeech = false;
      this._processor.onaudioprocess = (event) => this._onAudio(event);
      this._source.connect(this._processor);
      this._processor.connect(this._audioContext.destination);
      this.onstart?.();
      warmOwnerLocalStt().catch(() => undefined);
    } catch (error) {
      pendingStream?.getTracks?.().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
      if (this._lifecycleToken !== lifecycleToken || this._stopping || this._aborted) {
        await this._cleanup();
        return;
      }
      await this._cleanup();
      this.onerror?.({ error: normalizedError(error), detail: error });
      this.onend?.();
    }
  }

  _onAudio(event) {
    if (this._stopping || this._aborted) return;
    const input = event.inputBuffer.getChannelData(0);
    const copy = new Float32Array(input.length);
    copy.set(input);
    this._chunks.push(copy);

    let sum = 0;
    for (let index = 0; index < input.length; index += 1) sum += input[index] * input[index];
    const rms = Math.sqrt(sum / Math.max(1, input.length));
    const now = performance.now();
    if (rms >= SPEECH_RMS) {
      if (!this._heardSpeech) this._speechStartedAt = now;
      this._heardSpeech = true;
      this._lastSpeechAt = now;
    }

    if (this._heardSpeech && now - this._speechStartedAt >= MIN_UTTERANCE_MS && now - this._lastSpeechAt >= SILENCE_MS) {
      this.stop();
      return;
    }
    if (this._heardSpeech && now - this._speechStartedAt >= MAX_UTTERANCE_MS) this.stop();
  }

  _finish({ abort = false } = {}) {
    if (abort) this._aborted = true;
    if (this._finishPromise) return this._finishPromise;

    this._stopping = true;
    this._aborted = Boolean(abort);
    this._lifecycleToken += 1;
    const pendingStart = this._startPromise;

    const pending = (async () => {
      // Si start() todavía espera permiso/getUserMedia, primero invalidamos su
      // token y esperamos que cierre cualquier stream tardío. Así un reinicio no
      // puede superponer recursos con la sesión que se está cancelando.
      if (pendingStart) {
        try { await pendingStart; } catch { /* start informa sus propios errores */ }
      }

      const chunks = this._chunks;
      const heardSpeech = this._heardSpeech;
      const sampleRate = this._audioContext?.sampleRate || 48000;
      await this._cleanup();

      if (abort) {
        this.onerror?.({ error: "aborted" });
        this.onend?.();
        return;
      }
      if (!heardSpeech || !chunks.length) {
        this.onerror?.({ error: "no-speech" });
        this.onend?.();
        return;
      }

      this._processing = true;
      try {
        const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const merged = new Float32Array(total);
        let offset = 0;
        for (const chunk of chunks) { merged.set(chunk, offset); offset += chunk.length; }
        const audio = resampleLinear(merged, sampleRate, TARGET_SAMPLE_RATE);
        const response = await workerRequest("transcribe", { audio: audio.buffer }, [audio.buffer]);
        const text = String(response?.text || "").trim();
        if (text && !this._aborted) this.onresult?.(makeResultEvent(text));
        else if (!text && !this._aborted) this.onerror?.({ error: "no-speech" });
      } catch (error) {
        if (!this._aborted) this.onerror?.({ error: "local-stt", detail: error });
      } finally {
        this._processing = false;
        this.onend?.();
      }
    })();

    this._finishPromise = pending;
    return pending.finally(() => {
      if (this._finishPromise === pending) this._finishPromise = null;
    });
  }

  stop() {
    return this._finish({ abort: false });
  }

  abort() {
    return this._finish({ abort: true });
  }

  async _cleanup() {
    if (this._processor) {
      try { this._processor.disconnect(); } catch { /* noop */ }
      this._processor.onaudioprocess = null;
      this._processor = null;
    }
    if (this._source) {
      try { this._source.disconnect(); } catch { /* noop */ }
      this._source = null;
    }
    if (this._stream) {
      this._stream.getTracks().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
      this._stream = null;
    }
    if (this._audioContext) {
      try { await this._audioContext.close(); } catch { /* noop */ }
      this._audioContext = null;
    }
    this._chunks = [];
    this._source = null;
    this._processor = null;
  }
}
